import { useGet, usePut } from "@albertine/api-client";
import { DEFAULT_SERVICE_HOURS_ID, toExampleServiceHoursText } from "@albertine/types";
import {
    type ServiceHoursSetting,
    type ServiceHoursWeekdayMap,
    UpsertServiceHoursRequestSchema,
} from "@albertine/types/openapi";
import { useQueryClient } from "@tanstack/react-query";
import classNames from "classnames";
import deepEqual from "fast-deep-equal";
import { useEffect, useState } from "react";
import type { ServiceHourMacro } from "../../common/types/Macro";
import type { PartialServiceHours } from "../../common/types/ServiceHours";
import { replaceMacros } from "../../common/utils/string.util";
import { WeekdayCodeArray, WeekdayNameArray } from "../../common/utils/time.util";
import config from "../../config";
import { EventEmitter } from "../../EventEmitter";
import { ServiceHoursString, serviceHoursToString } from "../../utils/time.util";
import Button from "../Common/Buttons";
import Dropdown from "../Common/Dropdown";
import Toggle from "../Common/Toggle";
import "./ServiceHours.css";

type Macros = {
    description: string;
} & ServiceHourMacro;

type MessageEditorProperties = {
    initialMessage: string;
    exampleServiceHoursText: string;
    onChange: (message: string) => void;
};

function MessageEditor(props: MessageEditorProperties) {
    const { initialMessage, exampleServiceHoursText, onChange } = props;
    const [message, setMessage] = useState<string>("");

    const [messageCursorPosition, setMessageCursorPosition] = useState<number>(0);

    useEffect(() => {
        setMessage(initialMessage);
    }, [initialMessage]);

    function insertMacro(macro: string) {
        return () => {
            const newMessage = `${message.substring(
                0,
                messageCursorPosition,
            )}{${macro}}${message.substring(messageCursorPosition)}`;
            setMessage(newMessage);
            onChange(newMessage);
        };
    }

    const macros: Macros[] = [
        {
            key: "member_name",
            description: "member name",
            value: "Mary Member",
        },
        {
            key: "member_greeting",
            description: "member greeting",
            value: "Ms Member",
        },
        {
            key: "service_hours",
            description: "service hours",
            value: exampleServiceHoursText,
        },
    ];

    return (
        <div>
            <div className="service-hours__message-input service-hours__setting service-hours__settings-group">
                <textarea
                    value={message}
                    onClick={(event: any) => {
                        setMessageCursorPosition(event.target.selectionStart);
                    }}
                    onChange={(event) => {
                        setMessageCursorPosition(event.target.selectionStart);
                        const newMessage = event.target.value;
                        setMessage(newMessage);
                        onChange(newMessage);
                    }}
                />
                <div className="service-hours__message-input__macros">
                    <span>Add:</span>{" "}
                    {macros.map((m) => (
                        <button key={m.key} type="button" onClick={insertMacro(m.key)}>
                            {m.description}
                        </button>
                    ))}
                </div>
            </div>
            <h3>Message preview</h3>

            <img
                className="service-hours__message-preview-image"
                src={config.conciergeServiceHourAssetUrl}
                alt=""
            />

            <div>
                {replaceMacros(macros, message)
                    .split("\n")
                    .map((s, i) => (
                        <p key={i}>{s}</p>
                    ))}
            </div>
        </div>
    );
}

type ServiceHoursSettingProperties = {
    initialValue: PartialServiceHours;
    onChange: (hrs: PartialServiceHours) => void;
};

function ServiceHoursSettingInput(props: ServiceHoursSettingProperties) {
    const { initialValue, onChange } = props;
    const [input, setInput] = useState<string>("");
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [value, setValue] = useState<PartialServiceHours>();
    const [valid, setValid] = useState<boolean>(true);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    return (
        <div
            className={classNames(
                "service-hours__settings-group service-hours__hours-input",
                value?.enabled ? "enabled" : "disabled",
            )}
        >
            <div
                className={classNames(
                    "service-hours__setting service-hours__setting__input",
                    valid ? "valid" : "invalid",
                )}
            >
                <input
                    type="text"
                    disabled={!value?.enabled}
                    value={
                        value?.enabled
                            ? value && valid && !isEditing
                                ? serviceHoursToString(value.hours)
                                : input
                            : ""
                    }
                    placeholder="No service on this day"
                    onChange={(event) => {
                        const newHours = ServiceHoursString.safeParse(
                            event.target.value,
                        );
                        setInput(event.target.value);
                        setIsEditing(true);
                        if (newHours.success) {
                            const newValue = value
                                ? {
                                      ...value,
                                      hours: newHours.data,
                                  }
                                : {
                                      hours: newHours.data,

                                      enabled: false,
                                  };
                            setValue(newValue);
                            setValid(true);
                            onChange(newValue);
                        } else if (value) {
                            setValue({ ...value });
                            setValid(false);
                        }
                    }}
                    onBlur={() => {
                        setIsEditing(false);
                    }}
                />
                <div className="service-hours__hours-input__tz">London time</div>
                <div className="service-hours__toggle-indicator">
                    {value?.enabled ? "On" : "Off"}
                </div>
                <div className="service-hours__hours-input__toggle-button">
                    <Toggle
                        disabled={!valid}
                        checked={value?.enabled || false}
                        onChange={() => {
                            if (value) {
                                const newValue = {
                                    ...value,
                                    enabled: !value.enabled,
                                };
                                setValue(newValue);
                                onChange(newValue);
                            }
                        }}
                    />
                </div>
            </div>
            <div className="service-hours__hours-input__error">
                Please enter service hours in correct format: 08:30 - 18:00
            </div>
        </div>
    );
}

const DefaultMessage = `Dear {member_name},\n
During the complimentary pre-launch membership, our pre-launch programme core service hours are {service_hours}.\n
Please rest assured that your request has been received and your dedicated lifestyle manager will respond as soon as possible within these hours.\n
We look forward to servicing you upon our return.`;

const DefaultServiceHours: PartialServiceHours = {
    enabled: false,
    hours: {
        opening: { hour: 8, minute: 0 },
        closing: { hour: 20, minute: 0 },
    },
};

const weekdayByCode = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
] as const satisfies readonly (keyof ServiceHoursWeekdayMap)[];

const DefaultSettings: ServiceHoursSetting = {
    id: DEFAULT_SERVICE_HOURS_ID,
    enabled: false,
    serviceHours: {
        sunday: DefaultServiceHours,
        monday: DefaultServiceHours,
        tuesday: DefaultServiceHours,
        wednesday: DefaultServiceHours,
        thursday: DefaultServiceHours,
        friday: DefaultServiceHours,
        saturday: DefaultServiceHours,
    },
    memberMessageTemplate: DefaultMessage,
    exampleServiceHoursText: "Service hours are not set",
};

type GroupServiceHoursProperties = {
    id: string;
    initialSettings: ServiceHoursSetting | undefined;
    onDiscardNew: () => void;
};

function GroupServiceHours(props: GroupServiceHoursProperties) {
    const { id, initialSettings, onDiscardNew } = props;
    const queryClient = useQueryClient();
    const [settings, setSettings] = useState<ServiceHoursSetting>();
    const [edited, setEdited] = useState<boolean>(false);
    const { mutate: upsertServiceHours, isPending: isSaving } = usePut(
        "upsertServiceHours",
        {
            onSuccess: () => {
                void queryClient.invalidateQueries({ queryKey: ["listServiceHours"] });
                setEdited(false);
            },
            onError: () => {
                EventEmitter.emit({
                    name: "showToastMessage",
                    params: {
                        message: "Failed to save service hours",
                        type: "error",
                    },
                });
            },
        },
    );

    function isSomeDayEnabled(s: ServiceHoursSetting) {
        return Object.values(s.serviceHours).some((hrs) => hrs.enabled);
    }

    useEffect(() => {
        if (initialSettings) {
            setSettings(initialSettings);
            setEdited(false);
        } else {
            setEdited(true);
            setSettings({
                ...DefaultSettings,
                id,
            });
        }
    }, [id, initialSettings]);

    useEffect(() => {
        setEdited(!deepEqual(initialSettings, settings));
        if (settings && settings.enabled && !isSomeDayEnabled(settings)) {
            setSettings({ ...settings, enabled: false });
        }
    }, [id, settings, initialSettings]);

    if (!settings) return <div />;

    return (
        <div>
            {edited ? (
                <div className="service-hours__save">
                    <div className="service-hours__save__label">
                        {initialSettings
                            ? "Settings changed"
                            : "Group has no settings. Save to create settings for the group."}
                    </div>
                    <div>
                        <Button.N90
                            onClick={() => {
                                if (initialSettings) {
                                    setSettings(initialSettings);
                                    setEdited(false);
                                } else {
                                    onDiscardNew();
                                }
                            }}
                        >
                            Discard
                        </Button.N90>
                    </div>
                    <div>
                        <Button.P50
                            disabled={isSaving}
                            onClick={() => {
                                const parsed =
                                    UpsertServiceHoursRequestSchema.safeParse({
                                        enabled: settings.enabled,
                                        memberMessageTemplate:
                                            settings.memberMessageTemplate,
                                        serviceHours: settings.serviceHours,
                                    });
                                if (!parsed.success) {
                                    EventEmitter.emit({
                                        name: "showToastMessage",
                                        params: {
                                            message: "Failed to save service hours",
                                            type: "error",
                                        },
                                    });
                                    return;
                                }
                                upsertServiceHours({
                                    param: { id },
                                    json: parsed.data,
                                });
                            }}
                        >
                            Save
                        </Button.P50>
                    </div>
                </div>
            ) : (
                <div className="service-hours__save">
                    <div className="service-hours__save__label">No changes made</div>
                </div>
            )}
            {WeekdayCodeArray.map((code) => {
                const weekday = weekdayByCode[code];
                const day = WeekdayNameArray[code];
                if (!weekday || !day) return null;
                return (
                    <div key={weekday}>
                        <h3>{day}</h3>
                        <ServiceHoursSettingInput
                            initialValue={settings.serviceHours[weekday]}
                            onChange={(newValue) => {
                                const serviceHours = {
                                    ...settings.serviceHours,
                                    [weekday]: newValue,
                                };
                                setSettings({
                                    ...settings,
                                    serviceHours,
                                    exampleServiceHoursText:
                                        toExampleServiceHoursText(serviceHours),
                                });
                            }}
                        />
                    </div>
                );
            })}

            <h3>Automated after hours message</h3>
            <p>
                {settings.enabled ? "Out-of-office " : "If enabled out-of-office "}{" "}
                message is sent to members instantly outside of these service
                hours:&nbsp; {settings.exampleServiceHoursText}.
            </p>

            <div className="service-hours__settings-group service-hours__setting service-hours__toggle">
                <div className="service-hours__toggle__label">
                    Enable automated out of hours message
                </div>
                <div className="service-hours__toggle-indicator">
                    {settings.enabled ? "On" : "Off"}
                </div>
                <div className="service-hours__toggle__toggle">
                    <Toggle
                        disabled={!isSomeDayEnabled(settings)}
                        checked={settings.enabled}
                        onChange={() => {
                            setSettings({
                                ...settings,
                                enabled: !settings.enabled,
                            });
                        }}
                    />
                </div>
            </div>
            <MessageEditor
                initialMessage={settings.memberMessageTemplate}
                exampleServiceHoursText={settings.exampleServiceHoursText}
                onChange={(memberMessageTemplate) => {
                    setSettings({ ...settings, memberMessageTemplate });
                }}
            />
        </div>
    );
}

type SettingsMap = Record<string, ServiceHoursSetting | undefined>;

function ServiceHours() {
    const [group, setGroup] = useState<string>(DEFAULT_SERVICE_HOURS_ID);
    const [settingsMap, setSettingsMap] = useState<SettingsMap>({});
    const [selectedSettings, setSelectedSettings] = useState<ServiceHoursSetting>();

    const [groupOptions, setGroupOptions] = useState<[string, string][]>();

    const { data: membershipTiers = [] } = useGet("membershipTiers", {});
    const { data: serviceHoursData, isLoading: loading } = useGet(
        "listServiceHours",
        {},
    );
    const serviceHours = serviceHoursData?.results;

    useEffect(() => {
        const newOptions: [string, string][] = membershipTiers
            .filter((tier) => tier.id !== DEFAULT_SERVICE_HOURS_ID)
            .map((tier) => [tier.id, tier.name]);
        setGroupOptions([[DEFAULT_SERVICE_HOURS_ID, "Default / Trial"], ...newOptions]);
    }, [membershipTiers]);

    useEffect(() => {
        const newMap: SettingsMap = {};
        (serviceHours ?? []).forEach((s) => {
            newMap[s.id] = s;
        });
        setSettingsMap(newMap);
        setSelectedSettings(newMap[group]);
    }, [group, serviceHours]);

    if (loading) {
        return <div>Loading</div>;
    }

    return (
        <div className="service-hours">
            <h2>Service hours</h2>
            <p>
                Set times for when lifestyle managers are available for serving members.
            </p>
            <div className="service-hours__select-group">
                <div className="service-hours__select-group__label">Settings for</div>
                <Dropdown
                    entries={groupOptions}
                    currentValue={group}
                    onChange={(g) => {
                        if (!g) return;
                        setGroup(g);
                        setSelectedSettings(settingsMap[g]);
                    }}
                />
            </div>
            <GroupServiceHours
                initialSettings={selectedSettings}
                id={group}
                onDiscardNew={() => {
                    setGroup(DEFAULT_SERVICE_HOURS_ID);
                    setSelectedSettings(settingsMap[DEFAULT_SERVICE_HOURS_ID]);
                }}
            />
        </div>
    );
}

export default ServiceHours;
